/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import { useEffect } from "react"
import type { Params } from "react-router"
import { useLocation, useMatch, useNavigate } from "react-router"

import { rolesForRoute } from "../features/access-rights/access-rights"
import type { UserRole } from "../features/user/user-types"
import { AppRoutes } from "../routes/routes"
import { useAppState } from "../ui/app/app-hooks"
import type { ValueOf } from "./types"

/**
 * Loads the data needed by a route when it becomes active.
 *
 * The effect receives the route params and query string so route-specific
 * loaders can use values such as the `id` in `/application/detail/:id`.
 */
type RouteEffect = (params?: Params, urlSearchParams?: URLSearchParams) => void | Promise<void>

/**
 * Controls what the hook shows while the route effect is running.
 */
export const RouteLoading = {
    /**
     * Shows the route loading spinner in place of the view.
     */
    Spinner: "spinner",
    /**
     * Leaves loading UI to the effect or view.
     *
     * Use this when the loader manages its own spinner or the view shows
     * a more specific loading state.
     */
    None: "none"
} as const

export type RouteLoading = ValueOf<typeof RouteLoading>

interface UseRouteEffectOptions {
    /**
     * Controls the loading UI while the effect runs.
     *
     * @default RouteLoading.Spinner
     */
    loading?: RouteLoading
}

/**
 * Runs an effect when its route becomes active.
 *
 * Checks access before running the effect and optionally manages the route
 * loading state while the effect is in progress.
 */
export function useRouteEffect(
    route: AppRoutes,
    effect: RouteEffect,
    { loading = RouteLoading.Spinner }: UseRouteEffectOptions = {}
): void {
    const match = useMatch(route)
    const location = useLocation()
    const appState = useAppState()
    const navigate = useNavigate()

    // Depend only on route identity. `effect` is an inline callback at each call site,
    // so including it would restart data-loading effects on every render.
    // biome-ignore lint/correctness/useExhaustiveDependencies: see above
    useEffect(() => {
        if (!match) return

        const params = match.params ?? {}
        const searchParams = new URLSearchParams(location.search)

        // Redirect before running the effect when the route is not allowed.
        const isAccessAllowed = isRouteAllowed(route, appState.roles)
        if (!isAccessAllowed) {
            navigate(AppRoutes.AccessDenied)
            return
        }

        const showsSpinner = loading === RouteLoading.Spinner

        void (async () => {
            if (showsSpinner) appState.setRouteLoading(true)
            try {
                await effect(params, searchParams)
            } finally {
                if (showsSpinner) appState.setRouteLoading(false)
            }
        })()
    }, [match?.pathname, location.search])
}

/**
 * Checks whether the user's roles allow access to a route.
 *
 * @param route - The route to check.
 * @param roles - Roles held by the signed-in user.
 * @returns `true` when at least one user role is allowed for the route.
 */
function isRouteAllowed(route: AppRoutes, roles: UserRole[]): boolean {
    return rolesForRoute(route).some((r) => roles.includes(r))
}
